<template>
  <div class="feature-key-info-query">
    <!-- 輸入區域 -->
    <div class="query-section">
      <div class="flex items-center" style="gap: 10px">
        <BaseElInput
          v-model="internalFeatureKey"
          clearable
          placeholder="請輸入 featureKey (例如: admin.member.page)"
          @keyup.enter.native="queryFeatureKeyInfo"
        />
        <el-button
          type="primary"
          :loading="loading"
          :disabled="!internalFeatureKey || !internalFeatureKey.trim()"
          @click="queryFeatureKeyInfo"
        >
          查詢
        </el-button>
      </div>
    </div>

    <el-divider />

    <!-- 結果顯示區域 -->
    <div class="result-section">
      <!-- 載入狀態 -->
      <div v-if="loading" class="loading-state">
        <i class="el-icon-loading" />
        <span class="loading-text">查詢中...</span>
      </div>

      <!-- 錯誤狀態 -->
      <div v-else-if="error" class="error-state">
        <MaterialIcon color="red" size="24">error</MaterialIcon>
        <span class="error-text">{{ error }}</span>
        <el-button size="mini" type="text" @click="retryQuery">重試</el-button>
      </div>

      <!-- 查詢結果 -->
      <div v-else-if="queryResult" class="result-content">
        <div class="result-header">
          <MaterialIcon color="var(--success)" size="24">info</MaterialIcon>
          <span class="result-title">權限資訊查詢結果</span>
        </div>

        <!-- 權限計畫詳細資訊 -->
        <div v-if="queryResult.observabilityPlans && queryResult.observabilityPlans.length" class="plans-card">
          <h4>權限計畫 ({{ queryResult.observabilityPlans.length }})</h4>
          <div class="plans-list">
            <div
              v-for="plan in queryResult.observabilityPlans"
              :key="plan.key"
              class="plan-item"
              :class="{ 'effective': plan.effective, 'ineffective': !plan.effective }"
            >
              <div class="plan-header">
                <div class="plan-name">
                  <MaterialIcon
                    :color="plan.effective ? 'var(--success)' : 'var(--danger)'"
                    size="16"
                  >
                    {{ plan.effective ? 'check_circle' : 'cancel' }}
                  </MaterialIcon>
                  <span class="plan-title">{{ plan.name }}</span>
                </div>
                <div class="plan-meta">
                  <Tag :type="getPlanTypeColor(plan.type)" size="mini">{{ getPlanTypeLabel(plan.type) }}</Tag>
                  <span class="priority-badge">優先級: {{ plan.priority }}</span>
                </div>
              </div>
              <div class="plan-details">
                <code class="plan-key">{{ plan.key }}</code>
              </div>
            </div>
          </div>
        </div>

        <!-- 權限角色詳細資訊 -->
        <div v-if="queryResult.observabilityRoles && queryResult.observabilityRoles.length" class="roles-card">
          <h4>權限角色 ({{ queryResult.observabilityRoles.length }})</h4>
          <div class="roles-list">
            <div
              v-for="role in queryResult.observabilityRoles"
              :key="role.role"
              class="role-item"
              :class="{ 'effective': role.effective, 'ineffective': !role.effective }"
            >
              <div class="role-header">
                <div class="role-name">
                  <MaterialIcon
                    :color="role.effective ? 'var(--success)' : 'var(--danger)'"
                    size="16"
                  >
                    {{ role.effective ? 'check_circle' : 'cancel' }}
                  </MaterialIcon>
                  <span class="role-title">{{ role.name }}</span>
                </div>
                <Tag :type="getRoleTypeColor(role.type)" size="mini">{{ getRoleTypeLabel(role.type) }}</Tag>
              </div>
              <div class="role-details">
                <code class="role-key">{{ role.role }}</code>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- 初始狀態 -->
      <div v-else class="initial-state">
        <MaterialIcon color="gray" size="48">search</MaterialIcon>
        <p class="initial-text">請輸入 featureKey 開始查詢權限資訊</p>
      </div>
    </div>
  </div>
</template>

<script>
import { defineComponent, ref, getCurrentInstance, watch, nextTick } from 'vue'
import { GetFeatureKeyInfo } from '@/api/permissionGate'
import { useShop } from '@/use/shop'
import { formatDate } from '@/utils/date'
import Tag from '@/components/Tag/Tag.vue'
import MaterialIcon from '@/components/MaterialIcon.vue'

export default defineComponent({
  name: 'FeatureKeyInfoQuery',
  components: { Tag, MaterialIcon },
  props: {
    featureKey: {
      type: String,
      default: '',
    },
  },
  emits: ['clear-feature-key'],
  setup (props, { emit }) {
    const { shopId } = useShop()
    const instance = getCurrentInstance()

    // 響應式資料
    const internalFeatureKey = ref('')
    const loading = ref(false)
    const error = ref(null)
    const queryResult = ref(null)
    const lastQueriedKey = ref('')
    const queryTime = ref('')

    // 查詢權限資訊
    const queryFeatureKeyInfo = async () => {
      if (!internalFeatureKey.value || !internalFeatureKey.value.trim()) {
        return
      }

      loading.value = true
      error.value = null
      queryResult.value = null

      const [res, errMsg] = await GetFeatureKeyInfo({
        shopId: shopId.value,
        featureKey: internalFeatureKey.value.trim(),
      })

      if (errMsg) {
        window.$message.error(errMsg)
        return
      }

      queryResult.value = res
      lastQueriedKey.value = internalFeatureKey.value.trim()
      queryTime.value = formatDate(new Date(), 'YYYY-MM-DD HH:mm:ss')

      loading.value = false
    }

    // 重試查詢
    const retryQuery = () => {
      if (lastQueriedKey.value) {
        internalFeatureKey.value = lastQueriedKey.value
      }
      queryFeatureKeyInfo()
    }

    // 複製到剪貼簿
    const copyToClipboard = async (text) => {
      try {
        await navigator.clipboard.writeText(text)
        instance?.proxy?.$message.success('已複製到剪貼簿')
      } catch (err) {
        console.error('複製失敗:', err)
        instance?.proxy?.$message.error('複製失敗')
      }
    }

    // 取得計畫類型標籤
    const getPlanTypeLabel = (type) => {
      const typeMap = {
        default: '預設',
        customModule: '自訂模組',
        customPlan: '自訂計畫',
      }
      return typeMap[type] || type
    }

    // 取得計畫類型顏色
    const getPlanTypeColor = (type) => {
      const colorMap = {
        default: 'primary',
        customModule: 'warning',
        customPlan: 'info',
      }
      return colorMap[type] || 'default'
    }

    // 取得角色類型標籤
    const getRoleTypeLabel = (type) => {
      const typeMap = {
        basicRole: '基本角色',
        customRole: '自訂角色',
      }
      return typeMap[type] || type
    }

    // 取得角色類型顏色
    const getRoleTypeColor = (type) => {
      const colorMap = {
        basicRole: 'success',
        customRole: 'info',
      }
      return colorMap[type] || 'default'
    }

    // 監聽外部傳入的 featureKey
    watch(() => props.featureKey, async (newFeatureKey) => {
      if (newFeatureKey && newFeatureKey.trim()) {
        internalFeatureKey.value = newFeatureKey.trim()
        await nextTick()
        await queryFeatureKeyInfo()
        // 查詢完成後清空外部傳入的 featureKey
        emit('clear-feature-key')
      }
    }, { immediate: true })

    return {
      internalFeatureKey,
      loading,
      error,
      queryResult,
      lastQueriedKey,
      queryTime,
      queryFeatureKeyInfo,
      retryQuery,
      copyToClipboard,
      getPlanTypeLabel,
      getPlanTypeColor,
      getRoleTypeLabel,
      getRoleTypeColor,
    }
  },
})
</script>

<style lang="postcss" scoped>
.feature-key-info-query {
  @apply text-white;
}

.result-section {
  @apply min-h-[300px];
}

.loading-state {
  @apply flex items-center justify-center gap-3 p-8 text-center;
}

.loading-text {
  @apply text-gray-300;
}

.error-state {
  @apply flex items-center justify-center gap-3 p-8 text-center;
}

.error-text {
  @apply text-red-400;
}

.initial-state {
  @apply flex flex-col items-center justify-center gap-4 p-8 text-center;
}

.initial-text {
  @apply text-gray-400 text-lg;
}

.result-content {
  @apply space-y-6;
}

.result-header {
  @apply flex items-center gap-3 mb-4;
}

.result-title {
  @apply text-lg font-medium text-[#58f993];
}

.plans-card,
.roles-card {
  @apply bg-gray-800 rounded-lg p-4 border border-gray-700;
}

.plans-card h4,
.roles-card h4 {
  @apply text-[#58f993] text-base font-medium mb-3;
}

/* Element UI 樣式覆蓋 */
::v-deep .el-button--mini {
  @apply text-xs;
}

::v-deep .el-divider {
  @apply border-gray-600;
}

/* 權限計畫樣式 */
.plans-list {
  @apply space-y-3;
}

.plan-item {
  @apply bg-gray-900 border border-gray-600 rounded-lg p-3;
  @apply transition-colors duration-200;
}

.plan-item.effective {
  @apply border-green-600 bg-green-900 bg-opacity-20;
}

.plan-item.ineffective {
  @apply border-danger bg-gray-800;
}

.plan-header {
  @apply flex items-center justify-between mb-2;
}

.plan-name {
  @apply flex items-center gap-2;
}

.plan-title {
  @apply text-white font-medium;
}

.plan-meta {
  @apply flex items-center gap-2;
}

.priority-badge {
  @apply text-xs text-gray-400 bg-gray-700 px-2 py-1 rounded;
}

.plan-details {
  @apply mt-2;
}

.plan-key {
  @apply bg-gray-700 px-2 py-1 rounded text-xs font-mono text-blue-300;
}

/* 權限角色樣式 */
.roles-list {
  @apply space-y-3;
}

.role-item {
  @apply bg-gray-900 border border-gray-600 rounded-lg p-3;
  @apply transition-colors duration-200;
}

.role-item.effective {
  @apply border-green-600 bg-green-900 bg-opacity-20;
}

.role-item.ineffective {
  @apply border-danger bg-gray-800;
}

.role-header {
  @apply flex items-center justify-between mb-2;
}

.role-name {
  @apply flex items-center gap-2;
}

.role-title {
  @apply text-white font-medium;
}

.role-details {
  @apply mt-2;
}

.role-key {
  @apply bg-gray-700 px-2 py-1 rounded text-xs font-mono text-purple-300;
}
</style>
